import { BillingPreferencesForm } from '@features/billing-preferences/billing-preferences-form.ts';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { defaultTo, find, isNil, some } from 'lodash';
import React from 'react';

import { AnimatedNumber } from '@/components/animated-number.tsx';
import Nullish from '@/components/nullish.tsx';
import { Fee, FeeType, Service } from '@/types/api.ts';
import { formatCurrency } from '@/utils/currency.ts';

type FeeBreakdownProps = {
  services: Service[];
  fees: Fee[];
  serviceBillingPreferences: BillingPreferencesForm['services'];
};

/**
 * Displays the fees in an itemized view based on the billing preference selection made by the applicant.
 */
export const FeeBreakdown = ({
  fees,
  services,
  serviceBillingPreferences
}: FeeBreakdownProps) => {
  // Whether there are any prepaid billing preference selections made by the applicant.
  const hasPrepaidSelected = some(serviceBillingPreferences, service => service.billingPreference === 'prepaid');

  // Calculated total for all services based on billing preference selections.
  const serviceFeesTotal = services.reduce((sum, service) => {
    const isPrepaid = find(serviceBillingPreferences, billingPreference => billingPreference.id === service.id)?.billingPreference === 'prepaid';

    return sum + ((isPrepaid ? service.prepaidTotal : service.standardTotal) ?? 0);
  }, 0);

  // Calculated total for all other fees.
  const feesTotal = fees.reduce((sum, fee) => sum + fee.standardAmount, 0);

  // Final total required to begin service based on the applicants billing preferences.
  const total = feesTotal + serviceFeesTotal;

  return (
    <Box>
      <Typography variant="h2" gutterBottom>
        Fee Breakdown
      </Typography>
      <Typography color="text.secondary" variant="body2" marginBottom={2}>
        These are the charges required to start each service. You will be required to pay these before service begins.
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="body1">
                  Service
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1">
                  Total
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.map((service) => {
              const serviceBillingPreference = find(serviceBillingPreferences, (billingPreference => billingPreference.id === service.id));
              const isPrepaid = serviceBillingPreference?.billingPreference === 'prepaid';

              return (
                <React.Fragment key={service.id}>
                  <TableRow>
                    <TableCell sx={{ borderBottom: 'none', fontWeight: 500 }}>
                      {service.label}
                      {' '}
                      {service.description}
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none', fontWeight: 500 }}>
                      <FeeAmountDisplay value={(isPrepaid ? service.prepaidTotal : service.standardTotal) ?? 0} />
                    </TableCell>
                  </TableRow>
                  {service.fees.map((fee, index) => {
                    const lastGroup = index === service.fees.length - 1;
                    const notLastGroupSx = !lastGroup && { borderBottom: 'none' };
                    const lastGroupSx = lastGroup && { paddingBottom: 1 };
                    const amount = isPrepaid ? fee.prepaidAmount : fee.standardAmount;

                    const feeAmountCellDecorationStyles = !isNil(amount) ?
                        { textDecorationLine: 'underline', textDecorationStyle: 'dotted' } :
                        {};

                    return (
                      <TableRow sx={{ height: 4 }} key={fee.id}>
                        <TableCell sx={{ paddingLeft: 5, paddingY: 0.5, ...notLastGroupSx, ...lastGroupSx }}>{fee.description}</TableCell>
                        <TableCell sx={{ paddingY: 0.5, ...feeAmountCellDecorationStyles, ...notLastGroupSx, ...lastGroupSx }}>
                          <FeeCellContent fee={fee} amount={isPrepaid ? fee.prepaidAmount : fee.standardAmount} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {fees.map((fee) => {
              const feeAmountCellDecorationStyles = !isNil(fee.standardAmount) ?
                  { textDecorationLine: 'underline', textDecorationStyle: 'dotted' } :
                  { };
              return (
                <TableRow key={fee.id}>
                  <TableCell>{fee.description}</TableCell>
                  <TableCell sx={{ ...feeAmountCellDecorationStyles }}>
                    <FeeCellContent fee={fee} amount={fee.standardAmount} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell>
                <Typography variant="body1">
                  Total
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1">
                  <FeeAmountDisplay value={total} />
                </Typography>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      {hasPrepaidSelected && (
        <Alert severity="warning" sx={{ marginTop: 2 }}>
          <strong>Important:</strong>
          {' '}
          With Prepaid Billing if the credit balance falls below zero, a credit of $20.00 is required to restart service.
        </Alert>
      )}
    </Box>
  );
};

/**
 * Tooltip content by fee type.
 */
const TOOLTIP_TITLE_MAP: { [key in FeeType]: string } = {
  membership: 'One Membership is required for services.',
  deposit: 'Deposit amount is based on credit review.',
  connect: 'Fee required to connect service for the first time.',
  minimum_to_start: 'Minimum amount required as an account credit to start service.'
};

const FeeCellContent = ({ fee, amount }: { fee: Fee; amount: number }) => {
  if (isNil(amount)) return (
    <Nullish value={amount} />
  );

  return (
    <Tooltip title={TOOLTIP_TITLE_MAP[fee.type]}>
      <Typography>
        <FeeAmountDisplay value={amount} />
      </Typography>
    </Tooltip>
  );
};

const FeeAmountDisplay = ({ value }: { value?: number }) => {
  return (
    isNil(value) ?
        <Nullish value={value} /> :
        <AnimatedNumber value={value} formatter={value => defaultTo(formatCurrency(value), '')} />
  );
};
